/**
 * Protected Route component
 * Redirects to sign-in if user is not authenticated.
 *
 * Demo bypass: if `localStorage.demo_mode === 'true'` (set by the
 * "Continue in demo mode" button on /signin), or the URL carries
 * `?demo=1`, the guard becomes a pass-through. Backend admin
 * endpoints don't require auth in production today, so demo mode
 * gives full read/write access without going through the passcode
 * flow. Useful for sales demos and for keeping the platform
 * navigable while the auth pipeline is still being stabilized.
 */
import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuthStore } from '../store/authStore';
import LoadingSpinner from './LoadingSpinner';

interface ProtectedRouteProps {
  children: React.ReactNode;
}

function isDemoMode(): boolean {
  try {
    if (typeof window === 'undefined') return false;
    if (window.localStorage?.getItem('demo_mode') === 'true') return true;
    if (new URLSearchParams(window.location.search).get('demo') === '1') {
      window.localStorage?.setItem('demo_mode', 'true');
      return true;
    }
  } catch {
    /* swallow — sandboxed iframes etc. */
  }
  return false;
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ children }) => {
  const { isAuthenticated, isLoading } = useAuthStore();
  const location = useLocation();

  if (isDemoMode()) {
    return <>{children}</>;
  }

  // Show loading while checking authentication
  if (isLoading) {
    return <LoadingSpinner />;
  }

  // Redirect to sign-in if not authenticated
  if (!isAuthenticated) {
    return (
      <Navigate
        to="/signin"
        state={{ from: location.pathname }}
        replace
      />
    );
  }

  return <>{children}</>;
};

export default ProtectedRoute;